import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useMatches } from 'react-router-dom'

import { Match } from 'modules/core/lib/navigation/routes'
import type { ResourceHeadersPropsWithActions } from 'modules/core/lib/navigation/types'

import { FactorialOnePage } from './FactorialOnePage'
import { PageStatusProvider } from './FactorialOnePage/usePageStatus'
import { Body } from './_Body'
import { NestedBody } from './_NestedBody'
import {
  ResourceHeaderProvider,
  ResponsivePageProvider,
  RouteMetaWithPathname,
  useRouteMeta,
} from './hooks'

export type { Banner as ResponsivePageBanner } from './_Body'
export type { TransactionalActions as ResponsivePageTransactionalActions } from './_Body/_TransactionalActions'
export type { ResponsivePageActionsInterface } from './_PageHeader'

interface Props {
  children: React.ReactNode
}

const ResponsivePage = ({ children }: Props) => {
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const matches = useMatches() as Match[]
  const { pathname } = useLocation()
  const scrollRef = useRef<HTMLElement>(null)
  const [actionHandler, setActionHandler] = useState<(key: string) => void>()
  const [headerOverride, setHeaderOverride] =
    useState<Partial<ResourceHeadersPropsWithActions> | null>(null)

  useLayoutEffect(() => {
    scrollRef?.current?.scrollTo({ top: 0 })
  }, [pathname])

  const metas: RouteMetaWithPathname[] = useMemo(
    () =>
      matches
        .map<RouteMetaWithPathname | null>((match: Match) => {
          const meta = match.handle?.meta
          const resource =
            match.handle && 'resource' in match.handle ? match.handle.resource ?? false : false
          const pathnameParam = match.pathname

          if (!meta) return null

          return {
            pathname: pathnameParam,
            meta,
            resource,
          }
        })
        .filter((e) => e !== null),
    [matches]
  )

  const currentPageMeta = useMemo(() => metas.at(-1) || null, [metas])
  const meta = useRouteMeta(currentPageMeta)

  useEffect(() => {
    if (meta?.data && 'title' in meta.data) {
      // Treat 'show_nothing' as empty title for browser tab (used to hide breadcrumbs)
      const title = meta.data.title === 'show_nothing' ? '' : meta.data.title
      const prepend = title ? `${title} | ` : ''
      document.title = `${prepend}Factorial`
    }
  }, [meta?.data])

  useEffect(() => {
    return () => {
      document.title = 'Factorial'
    }
  }, [])

  // NOTE: this prevents re-rendering complete pages when meta is being computed.
  if (!meta && currentPageMeta) {
    return null
  }

  return (
    <ResponsivePageProvider
      value={{
        routeMeta: currentPageMeta,
        setActionHandler,
        headerOverride,
        setHeaderOverride,
      }}
    >
      <ResourceHeaderProvider value={{ headerOverride, setHeaderOverride }}>
        <PageStatusProvider>
          <FactorialOnePage onActionClick={actionHandler}>{children}</FactorialOnePage>
        </PageStatusProvider>
      </ResourceHeaderProvider>
    </ResponsivePageProvider>
  )
}

ResponsivePage.Body = Body
ResponsivePage.NestedBody = NestedBody

export { ResponsivePage }
